<template>
  <div
    class="us-table"
    :class="{ 'us-table--scrollable': scrollable }"
    data-test="us-table"
    role="region"
    tabindex="0"
  >
    <slot name="title" />
    <div class="us-table__scroll-wrapper">
      <table role="table">
      <thead role="rowgroup">
        <tr role="row">
          <th
            v-for="(col, i) in columns"
            :key="i"
            role="columnheader"
            class="body--p1--medium"
          >
            {{ col }}
          </th>
        </tr>
      </thead>
      <tbody role="rowgroup">
        <slot :columns="columns" />
      </tbody>
    </table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

export default defineComponent({
  name: 'USTable',
  props: {
    columns: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    scrollable: {
      type: Boolean,
      default: false
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@base/@motiv/fe-patterns/styles/table';

@use '@base/styles/v1.0/scss/foundations/breakpoint' as bp;
@use '@base/styles/v1.0/scss/foundations/color';
@use '@base/styles/v1.0/scss/foundations/motion';

$tr-sel: '::v-slotted(tr), tr';
$td-sel: ':deep(td)';

@include table.responsive-table(
  $data-label: data-label,
  $breakpoint: bp.$breakpoint--9,
  $label-basis: 45%,
  $table-sel: table,
  $thead-sel: thead,
  $tbody-sel: tbody,
  $tr-sel: $tr-sel,
  $th-sel: th,
  $td-sel: $td-sel
);

.us-table {
  width: 100%;
}

.us-table__scroll-wrapper {
  width: 100%;

  .us-table--scrollable & {
    @media (min-width: bp.$breakpoint--9) {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
  }
}

table {
  backface-visibility: visible; // Override CSS reset
  width: 100%;

  .us-table--scrollable .us-table__scroll-wrapper & {
    @media (min-width: bp.$breakpoint--9) {
      min-width: max-content;
    }
  }
}

thead {
  @media (min-width: bp.$breakpoint--9) {
    border-bottom: var(--line-weight--heavy) solid var(--color--neutral-a4);
  }
}

th,
#{$td-sel}:before {
  color: var(--color--primary-b3);
  text-transform: uppercase;
}

th,
#{$td-sel} {
  padding: var(--spacing--7);
}

tbody #{$tr-sel} {
  border: solid var(--line-weight--heavy) var(--color--neutral-a4);
  border-radius: 15px;
  color: var(--color--neutral-a1);
  margin-bottom: 20px;

  @media (min-width: bp.$breakpoint--9) {
    border: none;
    border-radius: 0;
  }

  #{$td-sel}::before {
    padding-right: 10px;
  }

  &:last-child {
    margin-bottom: 0;
  }
}

tbody {
  #{$td-sel} {
    font-size: var(--font-size-p1);
    font-weight: var(--font-weight-regular);
    line-height: var(--line-height-p1);
  }

  :deep(td) {
    justify-content: flex-start;
    text-align: left;
  }

  :deep(td:before) {
    min-width: 45%;
    padding-right: 10px;
  }

  :deep(tr) {
    border-radius: var(--border-radius--small);
    padding: 15px 0;
    transition: box-shadow motion.duration(7) ease-in-out;
    will-change: box-shadow;

    @media (min-width: bp.$breakpoint--9) {
      border-radius: 0;
      padding: 0;
    }
  }

  :deep(tr:hover, tr:focus) {
    @media (min-width: bp.$breakpoint--9) {
      box-shadow: var(--box-shadow--b1);

      /*
        Can't add border-radius to element with `display: table-row`.
        Below adds border-radius to children instead.
        However this doesn't work with box-shadow set at parent-level
        which has no border-radius...
      */
      /*
        & > td:first-child {
          background: blue;
          border-bottom-left-radius: var(--border-radius--small);
          border-top-left-radius: var(--border-radius--small);
        }
        & td:last-child {
          background: green;
          border-bottom-right-radius: var(--border-radius--small);
          border-top-right-radius: var(--border-radius--small);
        }
      */
    }
  }
}
</style>
